// icons
// next
import NextLink from 'next/link';
// @mui
import { Box, Card, LinearProgress, Stack, Typography } from '@mui/material';
import { m } from 'framer-motion';
import { ProjectProps } from 'src/@types/project';
import { TextMaxLine, varHover, varTranHover } from 'src/components';
import Image from 'src/components/NextSanityImage';
import { useResponsive } from 'src/hooks';
import Routes from 'src/routes';
import { format } from 'date-fns';

// ----------------------------------------------------------------------

type Props = {
  project: ProjectProps;
};

export default function ProjectItem({ project }: Props) {
  const { slug, title, header, projectCategory, ongoing, startDate, endDate } = project;
  const isMobile = useResponsive('down', 'md');

  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  //@ts-ignore
  const completion = Math.min(100, Math.round(((now - start) / (end - start)) * 100));

  return (
    <Card>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          py: 1.5,
          pl: 2,
          pr: 1.5,
          top: 0,
          width: 1,
          zIndex: 9,
          position: 'absolute',
        }}
      >
        {completion == 100 ? (
          <Stack
            spacing={0.5}
            direction="row"
            sx={{
              px: 1,
              borderRadius: 0.75,
              typography: 'caption',
              fontWeight: 'bold',
              bgcolor: 'success.main',
              color: (theme) => (theme.palette.mode === 'light' ? 'success.darker' : 'grey.800'),
            }}
          >
            Fullført
          </Stack>
        ) : (
          ongoing && (
            <Stack
              spacing={0.5}
              direction="row"
              sx={{
                px: 1,
                borderRadius: 0.75,
                typography: 'caption',
                fontWeight: 'bold',
                bgcolor: 'info.dark',
                color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
              }}
            >
              Pågående
            </Stack>
          )
        )}
      </Stack>

      <NextLink href={Routes.project('[slug]')} as={Routes.project(slug.current)}>
        <a>
          <Box sx={{ overflow: 'hidden' }}>
            <m.div variants={varHover(1.1)} transition={varTranHover()}>
              <Image
                alt={slug.current}
                src={header}
                ratio={isMobile ? '16/9' : '4/3'}
                layout="intrinsic"
                width={500}
                height={200}
              />
            </m.div>
          </Box>
        </a>
      </NextLink>

      <LinearProgress
        color={completion == 100 ? 'success' : 'info'}
        variant="determinate"
        sx={{
          width: 1,
          height: 10,
          borderRadius: 0,
          ...(completion == 100 && { bgcolor: 'success.main' }),
        }}
        value={completion}
      />

      <Stack spacing={0.5} sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body3" sx={{ color: 'text.secondary' }}>
            {projectCategory.name}
          </Typography>
          <Typography variant="body3" sx={{ color: 'text.secondary' }}>
            {format(start, 'MMMM yyyy')}
          </Typography>
        </Stack>

        <NextLink as={Routes.project(slug.current)} href={Routes.project('[slug]')} passHref>
          <TextMaxLine variant="h6" asLink>
            {title}
          </TextMaxLine>
        </NextLink>
      </Stack>
    </Card>
  );
}
